/**
 * QuestLog (Sirus 3.3.5a) — R232q22 Phase 3.
 *
 * Two-column journal panel mirroring the WoW 3.3.5a / Sirus client. Open
 * with `L` (or by emitting `aiwari-open-quest-log`). Layout:
 *
 *   ┌──────────────── 900 × 600 ───────────────┐
 *   │ Header (Cinzel large, gold)              │
 *   ├──────────┬───────────────────────────────┤
 *   │ Left     │ Right                         │
 *   │  list    │  details (description, objs, │
 *   │  (250)   │  rewards, action buttons)    │
 *   └──────────┴───────────────────────────────┘
 *
 * Categories on the left are collapsible (`По моему уровню (3-5)`,
 * `Подземелья`, `Ремёсла`, `Ежедневные`, `PvP`, `Завершённые`). Each
 * row shows quest title (gold), level (mono) and progress ratio.
 *
 * State sources:
 *   • `aiwari-quest-state:accepted` localStorage — array of
 *     `{ questId, progress, acceptedAt }` written by the legacy
 *     `components/QuestLog.tsx` and `NpcDialog`. We read it here as a
 *     compatibility bridge so the new Sirus log shows the same quests
 *     while a proper `useQuestLog` Zustand store is still pending.
 *   • `data/quests.ts QUESTS` — catalogue with descriptions/rewards.
 *   • `useNet` for player level (drives «По моему уровню» bucket).
 *   • `useQuestTracker` for the «Отслеживать» toggle.
 *
 * Deferred (separate work):
 *   • Server-side abandon RPC (currently localStorage-only).
 *   • Share-to-party broadcast via Colyseus.
 *   • Live objective progress from server (current behaviour: progress
 *     count comes from the cached object — matches legacy log).
 *   • Real category metadata (dungeon/profession/daily/pvp): catalogue
 *     today only carries `type`, so we infer category heuristically.
 */

import { useEffect, useMemo, useState } from 'react';
import { QUESTS, type Quest } from '../../data/quests.js';
import { ITEMS, RARITY_COLOUR } from '../../data/equipment.js';
import { useNet } from '../../state/net-store.js';
import { levelFromXp } from '../../data/leveling.js';
import { useQuestTracker } from '../../state/quest-tracker-store.js';

const ACCEPTED_KEY = 'aiwari-quest-state:accepted';
const COMPLETED_KEY = 'aiwari-quest-state:completed';

interface QuestProgress {
  questId: string;
  progress: number;
  acceptedAt: number;
}

type CategoryKey = 'level' | 'dungeon' | 'profession' | 'daily' | 'pvp' | 'done';

const CATEGORY_ORDER: CategoryKey[] = ['level', 'dungeon', 'profession', 'daily', 'pvp', 'done'];

const CATEGORY_LABEL: Record<CategoryKey, (level: number) => string> = {
  level: (lvl) => `По моему уровню (${Math.max(1, lvl - 2)}-${lvl + 2})`,
  dungeon: () => 'Подземелья',
  profession: () => 'Ремёсла',
  daily: () => 'Ежедневные',
  pvp: () => 'PvP',
  done: () => 'Завершённые',
};

/** Heuristic — infer category bucket from a quest id / type when explicit
 *  metadata is missing (most of the catalogue today). */
function categorise(q: Quest, level: number, isCompleted: boolean): CategoryKey {
  if (isCompleted) return 'done';
  const id = q.id.toLowerCase();
  if (id.includes('dungeon') || id.includes('raid') || id.includes('-dungeon-')) return 'dungeon';
  if (id.includes('craft') || id.includes('profession') || id.includes('-prof-')) return 'profession';
  if (id.includes('daily') || id.startsWith('daily-')) return 'daily';
  if (id.includes('pvp') || id.includes('-bg-') || id.includes('honor')) return 'pvp';
  if (level >= q.minLevel - 2 && level <= q.maxLevel + 2) return 'level';
  // Fallback: still attach to «По моему уровню» so something shows.
  return 'level';
}

function loadAccepted(): QuestProgress[] {
  try { return JSON.parse(localStorage.getItem(ACCEPTED_KEY) ?? '[]') as QuestProgress[]; } catch { return []; }
}
function saveAccepted(qs: QuestProgress[]) {
  try { localStorage.setItem(ACCEPTED_KEY, JSON.stringify(qs)); } catch {}
}
function loadCompleted(): string[] {
  try { return JSON.parse(localStorage.getItem(COMPLETED_KEY) ?? '[]') as string[]; } catch { return []; }
}

/** Required count for the quest's only objective. */
function requiredCount(q: Quest): number {
  return q.objective.count ?? 1;
}

export function QuestLog() {
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<CategoryKey>>(() => new Set(['done']));
  const [accepted, setAccepted] = useState<QuestProgress[]>(() => loadAccepted());
  const [completed, setCompleted] = useState<string[]>(() => loadCompleted());
  const [confirmAbandon, setConfirmAbandon] = useState<string | null>(null);
  const [mobileTab, setMobileTab] = useState<'list' | 'detail'>('list');

  const selfId = useNet((s) => s.selfId);
  const players = useNet((s) => s.players);
  const self = selfId ? players[selfId] : null;
  const xp = (self as { xp?: number } | null)?.xp ?? 0;
  const level = levelFromXp(xp).level;

  const tracked = useQuestTracker((s) => s.tracked);
  const trackQuest = useQuestTracker((s) => s.track);
  const untrackQuest = useQuestTracker((s) => s.untrack);

  // ── Open/close on `L`, plus react to external open events ────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'l' || e.key === 'L' || e.key === 'д' || e.key === 'Д') {
        setOpen((o) => !o);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    const onOpenEvent = (e: Event) => {
      const detail = (e as CustomEvent).detail as { questId?: string } | undefined;
      setOpen(true);
      if (detail?.questId) {
        setSelectedId(detail.questId);
        setMobileTab('detail');
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('aiwari-open-quest-log', onOpenEvent as EventListener);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('aiwari-open-quest-log', onOpenEvent as EventListener);
    };
  }, []);

  // Re-pull localStorage every time the panel opens — picks up changes
  // made while it was closed (NpcDialog accepts, mob-kill ticks, etc).
  useEffect(() => {
    if (!open) return;
    setAccepted(loadAccepted());
    setCompleted(loadCompleted());
  }, [open]);

  // ── Build category buckets ──────────────────────────────────────────
  const buckets = useMemo(() => {
    const out: Record<CategoryKey, Array<{ quest: Quest; progress: number; isDone: boolean }>> = {
      level: [], dungeon: [], profession: [], daily: [], pvp: [], done: [],
    };
    // Active quests
    for (const a of accepted) {
      const q = QUESTS.find((qq) => qq.id === a.questId);
      if (!q) continue;
      const cat = categorise(q, level, false);
      out[cat].push({ quest: q, progress: a.progress, isDone: false });
    }
    // Completed (read-only history)
    for (const cid of completed) {
      const q = QUESTS.find((qq) => qq.id === cid);
      if (!q) continue;
      out.done.push({ quest: q, progress: requiredCount(q), isDone: true });
    }
    return out;
  }, [accepted, completed, level]);

  const totalActive = accepted.length;

  // Pick a default selection so the right pane isn't blank.
  useEffect(() => {
    if (!open) return;
    if (selectedId) {
      // Still valid?
      const stillThere = QUESTS.find((q) => q.id === selectedId)
        && (accepted.some((a) => a.questId === selectedId) || completed.includes(selectedId));
      if (stillThere) return;
    }
    const firstActive = accepted[0]?.questId ?? completed[0] ?? null;
    setSelectedId(firstActive);
  }, [open, accepted, completed, selectedId]);

  if (!open) return null;

  const selected = selectedId
    ? QUESTS.find((q) => q.id === selectedId) ?? null
    : null;
  const selectedProgress = selectedId
    ? accepted.find((a) => a.questId === selectedId)?.progress ?? requiredCount(selected ?? ({ objective: {} } as Quest))
    : 0;
  const selectedIsDone = selectedId ? completed.includes(selectedId) : false;

  const toggleCategory = (key: CategoryKey) => {
    setCollapsed((cur) => {
      const next = new Set(cur);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const onAbandon = () => {
    if (!selectedId) return;
    setConfirmAbandon(selectedId);
  };

  const reallyAbandon = () => {
    if (!confirmAbandon) return;
    const next = accepted.filter((a) => a.questId !== confirmAbandon);
    saveAccepted(next);
    setAccepted(next);
    untrackQuest(confirmAbandon);
    if (selectedId === confirmAbandon) setSelectedId(next[0]?.questId ?? null);
    setConfirmAbandon(null);
  };

  const onShare = () => {
    if (!selectedId) return;
    // TODO: wire to Colyseus party broadcast — see deferred notes.
    window.dispatchEvent(new CustomEvent('aiwari-quest-share', { detail: { questId: selectedId } }));
    console.log('[QuestLog] share quest', selectedId);
  };

  const onTrackToggle = () => {
    if (!selectedId) return;
    if (tracked.has(selectedId)) untrackQuest(selectedId);
    else trackQuest(selectedId);
  };

  const isEmpty = totalActive === 0 && completed.length === 0;

  return (
    <div className="aiwari-questlog-backdrop" onClick={() => setOpen(false)}>
      <div className="aiwari-questlog aiwari-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ql-frame">
          {/* Header */}
          <div className="ql-header">
            <h2 className="ql-title">Карта мира — Журнал заданий</h2>
            <div className="ql-header-meta">
              <span className="ql-stat">
                Активные: <strong>{totalActive}</strong>
              </span>
              <span className="ql-stat ql-stat-dim">
                Завершено: <strong>{completed.length}</strong>
              </span>
              <button
                type="button"
                className="ql-close"
                aria-label="Закрыть"
                onClick={() => setOpen(false)}
              >×</button>
            </div>
          </div>

          {/* Mobile tabs */}
          <div className="ql-mobile-tabs">
            <button
              type="button"
              data-active={mobileTab === 'list'}
              onClick={() => setMobileTab('list')}
            >Список</button>
            <button
              type="button"
              data-active={mobileTab === 'detail'}
              onClick={() => setMobileTab('detail')}
              disabled={!selected}
            >Детали</button>
          </div>

          {/* Body */}
          {isEmpty ? (
            <div className="ql-empty">
              <div className="ql-empty-sigil" aria-hidden>⚜</div>
              <div className="ql-empty-text">
                Журнал героя пуст. Найди квестодателя в мире —<br />
                символы <span className="ql-empty-glyph">!</span> и <span className="ql-empty-glyph">?</span> на минимапе укажут путь.
              </div>
            </div>
          ) : (
            <div className="ql-body" data-mobile-tab={mobileTab}>
              {/* Left column — category list */}
              <aside className="ql-list">
                {CATEGORY_ORDER.map((cat) => {
                  const items = buckets[cat];
                  if (items.length === 0) return null;
                  const isCollapsed = collapsed.has(cat);
                  return (
                    <section key={cat} className="ql-category">
                      <button
                        type="button"
                        className="ql-category-header"
                        onClick={() => toggleCategory(cat)}
                        aria-expanded={!isCollapsed}
                      >
                        <span className="ql-category-arrow" data-collapsed={isCollapsed}>▾</span>
                        <span className="ql-category-label">{CATEGORY_LABEL[cat](level)}</span>
                        <span className="ql-category-count">{items.length}</span>
                      </button>
                      {!isCollapsed && (
                        <ul className="ql-category-list">
                          {items.map(({ quest, progress, isDone }, idx) => {
                            const max = requiredCount(quest);
                            const isSelected = quest.id === selectedId;
                            const isTracked = tracked.has(quest.id);
                            return (
                              <li key={quest.id}>
                                <button
                                  type="button"
                                  className="ql-entry"
                                  data-selected={isSelected}
                                  data-done={isDone}
                                  onClick={() => {
                                    setSelectedId(quest.id);
                                    setMobileTab('detail');
                                  }}
                                >
                                  <span className="ql-entry-medallion" aria-hidden>{idx + 1}</span>
                                  <span className="ql-entry-body">
                                    <span className="ql-entry-title">
                                      {isTracked ? <span className="ql-entry-pin" aria-hidden>✦</span> : null}
                                      {quest.title.ru}
                                    </span>
                                    <span className="ql-entry-objline">
                                      {isDone
                                        ? '✓ Завершено'
                                        : `${progress}/${max} ${quest.objective.target ?? ''}`}
                                    </span>
                                  </span>
                                  <span className="ql-entry-level">[{quest.minLevel}]</span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </section>
                  );
                })}
              </aside>

              {/* Right column — details */}
              <section className="ql-detail">
                {!selected ? (
                  <div className="ql-detail-empty">Выбери задание слева, чтобы прочитать его.</div>
                ) : (
                  <QuestDetail
                    quest={selected}
                    progress={selectedProgress}
                    isDone={selectedIsDone}
                    isTracked={tracked.has(selected.id)}
                    onAbandon={onAbandon}
                    onShare={onShare}
                    onTrackToggle={onTrackToggle}
                    onClose={() => setOpen(false)}
                  />
                )}
              </section>
            </div>
          )}

          {/* Confirm abandon modal */}
          {confirmAbandon && (
            <div className="ql-confirm-backdrop" onClick={() => setConfirmAbandon(null)}>
              <div className="ql-confirm" onClick={(e) => e.stopPropagation()}>
                <div className="ql-confirm-title">Покинуть задание?</div>
                <div className="ql-confirm-body">
                  Прогресс будет утерян. Задание можно будет взять снова у квестодателя.
                </div>
                <div className="ql-confirm-actions">
                  <button type="button" className="ql-btn ql-btn-ghost" onClick={() => setConfirmAbandon(null)}>Отмена</button>
                  <button type="button" className="ql-btn ql-btn-danger" onClick={reallyAbandon}>Покинуть</button>
                </div>
              </div>
            </div>
          )}
        </div>

        <style>{questLogCss}</style>
      </div>
    </div>
  );
}

// ── Right-pane detail block ──────────────────────────────────────────────

interface QuestDetailProps {
  quest: Quest;
  progress: number;
  isDone: boolean;
  isTracked: boolean;
  onAbandon: () => void;
  onShare: () => void;
  onTrackToggle: () => void;
  onClose: () => void;
}

function QuestDetail({
  quest,
  progress,
  isDone,
  isTracked,
  onAbandon,
  onShare,
  onTrackToggle,
  onClose,
}: QuestDetailProps) {
  const max = requiredCount(quest);
  const rewardItem = quest.reward.itemId ? ITEMS.find((i) => i.id === quest.reward.itemId) : undefined;

  // Synthetic 3-4 paragraph lore: real catalogue holds a single string;
  // we split on sentences to give the WoW-style multi-paragraph feel.
  const loreParas = useMemo(() => {
    const txt = quest.description.ru.trim();
    if (!txt) return ['Описание квеста пока не найдено в свитке.'];
    const sentences = txt.split(/(?<=[.!?])\s+/).filter(Boolean);
    if (sentences.length <= 1) return [txt];
    const para1 = sentences.slice(0, Math.ceil(sentences.length / 2)).join(' ');
    const para2 = sentences.slice(Math.ceil(sentences.length / 2)).join(' ');
    return [para1, para2];
  }, [quest.description.ru]);

  return (
    <div className="ql-detail-inner">
      <header className="qd-head">
        <h3 className="qd-title">{quest.title.ru}</h3>
        <div className="qd-meta">
          <span>Уровень <strong>{quest.minLevel}</strong></span>
          <span className="qd-meta-dim">рекомендован {quest.minLevel}-{quest.maxLevel}</span>
        </div>
        <div className="qd-giver">
          <span className="qd-giver-label">Квестодатель:</span>{' '}
          <span className="qd-giver-name">{quest.giverName.ru}</span>
        </div>
      </header>

      <div className="qd-scroll">
        <section className="qd-section qd-lore">
          {loreParas.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </section>

        <section className="qd-section">
          <h4 className="qd-section-head">Цели</h4>
          <ul className="qd-objectives">
            <li>
              <span className="qd-checkbox" data-done={progress >= max || isDone}>
                {(progress >= max || isDone) ? '✓' : ''}
              </span>
              <span className="qd-obj-text">
                {quest.type === 'kill' && `Убить ${quest.objective.target ?? 'цель'}`}
                {quest.type === 'gather' && `Собрать ${quest.objective.target ?? 'предмет'}`}
                {quest.type === 'deliver' && `Доставить ${quest.objective.target ?? 'посылку'}`}
                {quest.type === 'visit' && `Достичь места (${quest.objective.pos?.x.toFixed(0) ?? '?'}, ${quest.objective.pos?.z.toFixed(0) ?? '?'})`}
              </span>
              <span className="qd-obj-count">
                {isDone ? `${max}/${max}` : `${Math.min(progress, max)}/${max}`}
              </span>
            </li>
          </ul>
        </section>

        <section className="qd-section">
          <h4 className="qd-section-head">Награда</h4>
          <div className="qd-rewards">
            <div className="qd-reward-pill">
              <span className="qd-reward-icon" aria-hidden>✦</span>
              <span>+{quest.reward.xp} опыта</span>
            </div>
            {quest.reward.gold ? (
              <div className="qd-reward-pill">
                <span className="qd-reward-icon" aria-hidden>🪙</span>
                <span>{quest.reward.gold} золота</span>
              </div>
            ) : null}
            {quest.reward.realmPoints ? (
              <div className="qd-reward-pill">
                <span className="qd-reward-icon" aria-hidden>⚔</span>
                <span>+{quest.reward.realmPoints} ОЧ королевства</span>
              </div>
            ) : null}
            {rewardItem ? (
              <div
                className="qd-reward-item"
                style={{
                  borderColor: RARITY_COLOUR[rewardItem.rarity] ?? '#888',
                  color: RARITY_COLOUR[rewardItem.rarity] ?? '#fff',
                }}
                title={rewardItem.name.en}
              >
                <span className="aiwari-icon-slot" aria-hidden>
                  <span className="qd-reward-sigil">{rewardItem.sigil}</span>
                </span>
                <span className="qd-reward-item-name">{rewardItem.name.ru}</span>
              </div>
            ) : null}
          </div>
        </section>
      </div>

      <footer className="qd-actions">
        <button
          type="button"
          className="ql-btn ql-btn-danger"
          onClick={onAbandon}
          disabled={isDone}
        >Покинуть</button>
        <button
          type="button"
          className="ql-btn ql-btn-ghost"
          onClick={onShare}
          disabled={isDone}
        >Поделиться</button>
        <button
          type="button"
          className="ql-btn ql-btn-gold"
          onClick={onTrackToggle}
          disabled={isDone}
          data-active={isTracked}
        >{isTracked ? 'Не отслеживать' : 'Отслеживать'}</button>
        <button
          type="button"
          className="ql-btn ql-btn-ghost"
          onClick={onClose}
        >Закрыть</button>
      </footer>
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────

const questLogCss = `
.aiwari-questlog-backdrop {
  position: fixed;
  inset: 0;
  z-index: 40;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
}
.aiwari-questlog {
  width: 900px;
  height: 600px;
  max-width: 95vw;
  max-height: 90vh;
  font-family: var(--aiwari-font-body);
  color: var(--aiwari-fg);
  position: relative;
  padding: 0;
  overflow: hidden;
}
.aiwari-questlog .ql-frame {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 14px 16px 14px;
  box-sizing: border-box;
}
.aiwari-questlog .ql-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 4px 10px;
  margin: -6px -8px 10px;
  border-bottom: 1px solid hsl(44 100% 49% / 0.30);
  background: linear-gradient(180deg,
    hsl(44 100% 14% / 0.55),
    hsl(44 100% 6% / 0.30));
  border-radius: 4px 4px 0 0;
  padding-left: 14px;
  padding-right: 14px;
}
.aiwari-questlog .ql-title {
  font-family: var(--aiwari-font-display);
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: var(--aiwari-gold-soft);
  letter-spacing: 0.10em;
  text-shadow: 0 1px 0 #000, 0 0 8px rgba(250, 179, 0, 0.35);
  text-transform: uppercase;
}
.aiwari-questlog .ql-header-meta {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 12px;
}
.aiwari-questlog .ql-stat strong { color: var(--aiwari-gold-soft); margin-left: 4px; }
.aiwari-questlog .ql-stat-dim { color: var(--aiwari-fg-muted); }
.aiwari-questlog .ql-close {
  background: transparent;
  border: 1px solid var(--aiwari-frame-edge);
  color: var(--aiwari-fg-muted);
  width: 28px;
  height: 28px;
  border-radius: 3px;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
}
.aiwari-questlog .ql-close:hover { color: var(--aiwari-gold); border-color: var(--aiwari-gold); }

.aiwari-questlog .ql-mobile-tabs {
  display: none;
  gap: 6px;
  margin-bottom: 8px;
}
.aiwari-questlog .ql-mobile-tabs button {
  flex: 1;
  padding: 6px 8px;
  font-family: var(--aiwari-font-caps);
  font-size: 11px;
  letter-spacing: 0.08em;
  background: transparent;
  border: 1px solid var(--aiwari-frame-edge);
  color: var(--aiwari-fg-muted);
  cursor: pointer;
  border-radius: 2px;
}
.aiwari-questlog .ql-mobile-tabs button[data-active='true'] {
  color: var(--aiwari-gold);
  border-color: var(--aiwari-gold);
  background: rgba(250, 179, 0, 0.06);
}

.aiwari-questlog .ql-body {
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: 12px;
  flex: 1;
  min-height: 0;
}
.aiwari-questlog .ql-list {
  border-right: 1px solid var(--aiwari-frame-edge);
  padding-right: 6px;
  overflow-y: auto;
  min-height: 0;
}
.aiwari-questlog .ql-category {
  margin-bottom: 6px;
}
.aiwari-questlog .ql-category-header {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  background: rgba(232, 199, 123, 0.05);
  border: 1px solid transparent;
  cursor: pointer;
  font-family: var(--aiwari-font-caps);
  font-size: 11px;
  letter-spacing: 0.08em;
  color: var(--aiwari-gold-soft);
  text-transform: uppercase;
  text-align: left;
}
.aiwari-questlog .ql-category-header:hover {
  border-color: var(--aiwari-frame-edge);
  background: rgba(232, 199, 123, 0.08);
}
.aiwari-questlog .ql-category-arrow {
  display: inline-block;
  transition: transform 120ms linear;
  color: var(--aiwari-gold);
  font-size: 10px;
}
.aiwari-questlog .ql-category-arrow[data-collapsed='true'] { transform: rotate(-90deg); }
.aiwari-questlog .ql-category-label { flex: 1; }
.aiwari-questlog .ql-category-count {
  font-family: var(--aiwari-font-mono);
  font-size: 10px;
  color: var(--aiwari-fg-muted);
}
.aiwari-questlog .ql-category-list {
  list-style: none;
  margin: 4px 0 0;
  padding: 0;
}
.aiwari-questlog .ql-entry {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 5px 6px 5px 4px;
  background: transparent;
  border: 1px solid transparent;
  border-left: 3px solid transparent;
  cursor: pointer;
  text-align: left;
  font-size: 12px;
  color: var(--aiwari-fg);
  border-radius: 2px;
  gap: 8px;
  transition: background-color 80ms linear, border-color 80ms linear;
}
.aiwari-questlog .ql-entry:hover {
  background: rgba(232, 199, 123, 0.06);
  border-color: var(--aiwari-frame-edge);
}
.aiwari-questlog .ql-entry[data-selected='true'] {
  background: linear-gradient(90deg,
    hsl(44 100% 49% / 0.20),
    hsl(44 100% 49% / 0.05) 80%);
  border-color: hsl(44 100% 49% / 0.50);
  border-left-color: var(--aiwari-gold);
  box-shadow: inset 0 0 6px rgba(250, 179, 0, 0.20);
}
.aiwari-questlog .ql-entry[data-done='true'] .ql-entry-title { color: var(--aiwari-fg-muted); text-decoration: line-through; }
.aiwari-questlog .ql-entry-medallion {
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%,
    hsl(48 100% 65%) 0%,
    hsl(44 100% 45%) 55%,
    hsl(36 80% 28%) 100%);
  border: 1px solid hsl(36 100% 18%);
  font-family: var(--aiwari-font-mono);
  font-size: 10px;
  font-weight: 800;
  color: hsl(36 100% 8%);
  text-shadow: 0 1px 0 hsl(48 100% 80% / 0.6);
  box-shadow:
    inset 0 1px 0 hsl(48 100% 90% / 0.5),
    inset 0 -1px 0 hsl(36 100% 14% / 0.6);
}
.aiwari-questlog .ql-entry[data-done='true'] .ql-entry-medallion {
  background: radial-gradient(circle at 30% 30%, hsl(120 40% 50%), hsl(120 40% 25%));
  color: hsl(120 80% 95%);
}
.aiwari-questlog .ql-entry-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}
.aiwari-questlog .ql-entry-title {
  font-family: var(--aiwari-font-display);
  font-size: 12px;
  font-weight: 600;
  color: var(--aiwari-gold);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 4px;
  text-shadow: 0 1px 0 #000;
}
.aiwari-questlog .ql-entry[data-selected='true'] .ql-entry-title {
  color: var(--aiwari-gold-soft);
  text-shadow: 0 0 6px hsl(44 100% 49% / 0.5), 0 1px 0 #000;
}
.aiwari-questlog .ql-entry-pin {
  color: var(--aiwari-gold);
  font-size: 10px;
}
.aiwari-questlog .ql-entry-objline {
  font-family: var(--aiwari-font-body);
  font-size: 11px;
  color: var(--aiwari-fg-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.aiwari-questlog .ql-entry[data-selected='true'] .ql-entry-objline {
  color: var(--aiwari-fg);
}
.aiwari-questlog .ql-entry-level {
  flex: 0 0 auto;
  font-family: var(--aiwari-font-mono);
  font-size: 10px;
  color: var(--aiwari-gold-dark);
}

.aiwari-questlog .ql-detail {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.aiwari-questlog .ql-detail-empty {
  padding: 40px;
  text-align: center;
  color: var(--aiwari-fg-muted);
  font-style: italic;
}
.aiwari-questlog .ql-detail-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.aiwari-questlog .qd-head { padding: 4px 8px 10px; border-bottom: 1px solid var(--aiwari-frame-edge); }
.aiwari-questlog .qd-title {
  margin: 0 0 4px;
  font-family: var(--aiwari-font-display);
  font-size: 22px;
  font-weight: 600;
  color: var(--aiwari-gold);
  letter-spacing: 0.04em;
  text-shadow: 0 1px 0 #000, 0 0 8px rgba(250, 179, 0, 0.30);
}
.aiwari-questlog .qd-meta {
  display: flex;
  gap: 10px;
  font-size: 11px;
  color: var(--aiwari-fg);
  margin-bottom: 2px;
}
.aiwari-questlog .qd-meta-dim { color: var(--aiwari-fg-muted); }
.aiwari-questlog .qd-meta strong { color: var(--aiwari-gold-soft); }
.aiwari-questlog .qd-giver { font-size: 12px; color: var(--aiwari-fg-muted); }
.aiwari-questlog .qd-giver-name { color: var(--aiwari-fg); font-weight: 600; }

.aiwari-questlog .qd-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 10px 12px 4px 8px;
  min-height: 0;
}
.aiwari-questlog .qd-section { margin-bottom: 14px; }
.aiwari-questlog .qd-section-head {
  margin: 0 0 6px;
  font-family: var(--aiwari-font-caps);
  font-size: 11px;
  letter-spacing: 0.1em;
  color: var(--aiwari-gold-soft);
  text-transform: uppercase;
}
.aiwari-questlog .qd-lore p {
  margin: 0 0 8px;
  font-family: var(--aiwari-font-body);
  font-style: italic;
  font-size: 14px;
  line-height: 1.55;
  color: var(--aiwari-fg);
}
.aiwari-questlog .qd-objectives {
  list-style: none;
  margin: 0;
  padding: 0;
}
.aiwari-questlog .qd-objectives li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  font-size: 13px;
}
.aiwari-questlog .qd-checkbox {
  width: 14px;
  height: 14px;
  border: 1px solid var(--aiwari-gold-dark);
  border-radius: 2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: var(--aiwari-gold);
  background: rgba(0, 0, 0, 0.4);
}
.aiwari-questlog .qd-checkbox[data-done='true'] {
  border-color: var(--aiwari-gold);
  background: rgba(250, 179, 0, 0.18);
}
.aiwari-questlog .qd-obj-text { flex: 1; color: var(--aiwari-fg); }
.aiwari-questlog .qd-obj-count {
  font-family: var(--aiwari-font-mono);
  font-size: 12px;
  color: var(--aiwari-gold-soft);
}

.aiwari-questlog .qd-rewards {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.aiwari-questlog .qd-reward-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border: 1px solid var(--aiwari-frame-edge);
  border-radius: 2px;
  background: rgba(0, 0, 0, 0.3);
  font-size: 12px;
  color: var(--aiwari-fg);
}
.aiwari-questlog .qd-reward-icon { color: var(--aiwari-gold); }
.aiwari-questlog .qd-reward-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px 4px 4px;
  border: 1px solid;
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.4);
  font-size: 12px;
  font-weight: 600;
}
.aiwari-questlog .qd-reward-item .aiwari-icon-slot {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.aiwari-questlog .qd-reward-sigil {
  font-family: var(--aiwari-font-display);
  font-size: 13px;
  color: var(--aiwari-gold);
}

.aiwari-questlog .qd-actions {
  display: flex;
  gap: 8px;
  padding: 10px 0 0;
  border-top: 1px solid var(--aiwari-frame-edge);
  margin-top: auto;
}
.aiwari-questlog .ql-btn {
  flex: 1;
  padding: 8px 12px;
  font-family: var(--aiwari-font-caps);
  font-size: 12px;
  letter-spacing: 0.08em;
  border: 1px solid var(--aiwari-frame-edge);
  background: linear-gradient(180deg, #1a1410, #0B0907);
  color: var(--aiwari-fg);
  cursor: pointer;
  border-radius: 2px;
  text-transform: uppercase;
  transition: border-color 80ms linear, color 80ms linear, box-shadow 80ms linear;
}
.aiwari-questlog .ql-btn:hover:not(:disabled) {
  border-color: var(--aiwari-gold);
  color: var(--aiwari-gold);
  box-shadow: 0 0 8px rgba(250, 179, 0, 0.25);
}
.aiwari-questlog .ql-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.aiwari-questlog .ql-btn-gold {
  border-color: var(--aiwari-gold-dark);
  color: var(--aiwari-gold);
}
.aiwari-questlog .ql-btn-gold[data-active='true'] {
  background: linear-gradient(180deg, #4a3408, #2a1f08);
  color: var(--aiwari-gold-soft);
  border-color: var(--aiwari-gold);
  box-shadow: 0 0 8px rgba(250, 179, 0, 0.35) inset;
}
.aiwari-questlog .ql-btn-danger { color: hsl(0 70% 65%); border-color: hsl(0 50% 30%); }
.aiwari-questlog .ql-btn-danger:hover:not(:disabled) {
  border-color: hsl(0 80% 55%);
  color: hsl(0 90% 75%);
  box-shadow: 0 0 8px hsl(0 80% 50% / 0.4);
}
.aiwari-questlog .ql-btn-ghost { color: var(--aiwari-fg-muted); }

.aiwari-questlog .ql-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 20px;
  gap: 16px;
}
.aiwari-questlog .ql-empty-sigil {
  font-size: 64px;
  color: var(--aiwari-gold);
  text-shadow: 0 0 18px rgba(250, 179, 0, 0.45);
  font-family: var(--aiwari-font-display);
}
.aiwari-questlog .ql-empty-text {
  font-family: var(--aiwari-font-body);
  font-size: 15px;
  font-style: italic;
  color: var(--aiwari-fg-muted);
  line-height: 1.6;
}
.aiwari-questlog .ql-empty-glyph {
  display: inline-block;
  width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  border-radius: 50%;
  background: var(--aiwari-gold);
  color: #1a1003;
  font-weight: 700;
  font-family: var(--aiwari-font-display);
  margin: 0 2px;
}

.aiwari-questlog .ql-confirm-backdrop {
  position: absolute;
  inset: 0;
  z-index: 5;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
}
.aiwari-questlog .ql-confirm {
  width: 360px;
  max-width: 90%;
  background: var(--aiwari-bg-card-solid);
  border: 1px solid var(--aiwari-gold-dark);
  border-radius: 4px;
  padding: 20px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.7), 0 0 16px rgba(250, 179, 0, 0.15);
}
.aiwari-questlog .ql-confirm-title {
  font-family: var(--aiwari-font-display);
  font-size: 16px;
  font-weight: 600;
  color: var(--aiwari-gold);
  margin-bottom: 8px;
}
.aiwari-questlog .ql-confirm-body {
  font-size: 13px;
  color: var(--aiwari-fg);
  margin-bottom: 16px;
  line-height: 1.5;
}
.aiwari-questlog .ql-confirm-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}
.aiwari-questlog .ql-confirm-actions .ql-btn { flex: 0 0 auto; min-width: 110px; }

@media (max-width: 760px), (pointer: coarse) and (max-width: 900px) {
  .aiwari-questlog {
    width: 100vw;
    height: 100vh;
    max-width: 100vw;
    max-height: 100vh;
  }
  .aiwari-questlog .ql-mobile-tabs { display: flex; }
  .aiwari-questlog .ql-body { grid-template-columns: 1fr; }
  .aiwari-questlog .ql-list { border-right: none; padding-right: 0; }
  .aiwari-questlog .ql-body[data-mobile-tab='list'] .ql-detail { display: none; }
  .aiwari-questlog .ql-body[data-mobile-tab='detail'] .ql-list { display: none; }
}
`;
